import React, { FC, useState } from "react";
import {
  Box,
  Button,
  Popover,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DateRange, Range, RangeKeyDict } from "react-date-range";
import {
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  format,
  isSameDay,
  startOfYear,
  subYears,
  endOfDay,
} from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export interface DateRangeValue {
  startDate: Date;
  endDate: Date;
}

interface DateRangeSelectorProps {
  onChange: (range: DateRangeValue | null) => void;
  value?: DateRangeValue | null;
  /** Match MUI small Select height when used in filter bars */
  compact?: boolean;
}

const DateRangeSelector: FC<DateRangeSelectorProps> = ({
  onChange,
  value,
  compact = false,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const primary = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const defaultRange: Range = {
    startDate: subDays(new Date(), 7),
    endDate: new Date(),
    key: "selection",
  };

  const [range, setRange] = useState<Range>(value ?? defaultRange);
  const [tempRange, setTempRange] = useState<Range>(value ?? defaultRange);

  const presets = [
    {
      label: "1W",
      range: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }),
    },
    {
      label: "1M",
      range: () => ({
        startDate: startOfMonth(subMonths(new Date(), 1)),
        endDate: endOfMonth(new Date()),
      }),
    },
    {
      label: "1Y",
      range: () => ({
        startDate: subYears(new Date(), 1),
        endDate: new Date(),
      }),
    },
    {
      label: "Today",
      range: () => ({ startDate: new Date(), endDate: new Date() }),
    },
    {
      label: "This Month",
      range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      }),
    },
    {
      label: "YTD",
      range: () => ({
        startDate: startOfYear(new Date()),
        endDate: new Date(),
      }),
    },
    {
      label: "Last 7 Days",
      range: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }),
    },
    {
      label: "Last Month",
      range: () => {
        const start = startOfMonth(subMonths(new Date(), 1));
        const end = endOfMonth(subMonths(new Date(), 1));
        return { startDate: start, endDate: end };
      },
    },
  ];

  const isPresetActive = (preset: (typeof presets)[number]) => {
    const presetRange = preset.range();
    return (
      isSameDay(tempRange.startDate!, presetRange.startDate) &&
      isSameDay(tempRange.endDate!, presetRange.endDate)
    );
  };

  const handleSelect = (ranges: RangeKeyDict) => {
    const selection = ranges.selection;
    if (selection.startDate && selection.endDate) {
      setTempRange(selection);
    }
  };

  const handlePresetClick = (preset: (typeof presets)[number]) => {
    const newRange = preset.range();
    setTempRange({ ...newRange, key: "selection" });
  };

  const handleApply = () => {
    setRange(tempRange);
    onChange({
      startDate: tempRange.startDate!,
      endDate: tempRange.endDate!,
    });
    setAnchorEl(null);
  };

  const handleClear = () => {
    setRange(defaultRange);
    setTempRange(defaultRange);
    onChange(null);
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setTempRange(range);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const displayRange = value ?? range;
  const formatted =
    value == null
      ? "All time"
      : displayRange.startDate && displayRange.endDate
        ? `${format(displayRange.startDate, "MMM d, yyyy")} – ${format(
            displayRange.endDate,
            "MMM d, yyyy",
          )}`
        : "Select Date Range";

  return (
    <>
      <Button
        variant="outlined"
        size={compact ? "small" : "medium"}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          textTransform: "none",
          minWidth: compact ? 140 : 200,
          height: compact ? 40 : undefined,
          px: 1.5,
          justifyContent: "space-between",
          color: "text.primary",
          borderColor: "divider",
          bgcolor: "background.paper",
          whiteSpace: "nowrap",
          "&:hover": { borderColor: primary, bgcolor: "background.paper" },
        }}
      >
        {formatted}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
            width: isSmall ? "95vw" : "auto",
            maxWidth: "90vw",
            overflowX: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Stack
          direction={isSmall ? "column" : "row"}
          spacing={isSmall ? 2 : 0}
          alignItems="stretch"
        >
          <Box sx={{ pr: 2, borderRight: "1px solid #eee", minWidth: 140 }}>
            {presets.map((preset) => {
              const active = isPresetActive(preset);
              return (
                <Button
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  size="small"
                  sx={{
                    display: "block",
                    textTransform: "none",
                    justifyContent: "flex-start",
                    width: "100%",
                    mb: 0.6,
                    border: `1px solid ${primary}`,
                    color: active ? "white" : primary,
                    backgroundColor: active ? primary : "white",
                    "&:hover": {
                      backgroundColor: primaryDark,
                      color: "white",
                    },
                  }}
                >
                  {preset.label}
                </Button>
              );
            })}
            <Button
              onClick={handleClear}
              size="small"
              sx={{
                display: "block",
                textTransform: "none",
                width: "100%",
                mt: 1,
                color: "text.secondary",
              }}
            >
              Clear
            </Button>
          </Box>

          <Box>
            <DateRange
              onChange={handleSelect}
              moveRangeOnFirstSelection={false}
              ranges={[tempRange]}
              maxDate={endOfDay(new Date())}
              direction={isSmall ? "vertical" : "horizontal"}
              showMonthAndYearPickers
              rangeColors={[primary]}
            />

            <Divider sx={{ mt: 2, mb: 1 }} />

            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Button
                onClick={handleClear}
                size="small"
                sx={{
                  color: "text.secondary",
                  textTransform: "none",
                }}
              >
                Clear
              </Button>
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={handleCancel}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    color: primary,
                    border: `1px solid ${primary}`,
                    "&:hover": { backgroundColor: primaryDark, color: "white" },
                    textTransform: "none",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApply}
                  variant="contained"
                  size="small"
                  color="primary"
                  sx={{
                    textTransform: "none",
                  }}
                  disabled={!tempRange.startDate || !tempRange.endDate}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};

export default DateRangeSelector;
